import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { compose } from 'redux';
import { omit } from 'lodash';

import { withClickOutside } from 'decorators';
import { GoogleMap } from 'components/core/GoogleMap';
import { SearchMarker } from 'components/search/SearchMap';
import { isLarge } from 'utils/breakpoints';
import isBrowser from 'utils/isBrowser';
import { Text } from 'components/core/Typography';

import './SearchMap.scss';

export function SearchMap({
	className,
	facilities,
	baseUrl,
	facilitySelected,
	handleFacilityDeselected,
	handleFacilitySelected,
	handlePinSelected,
	handleSearch,
	location,
	searchType,
	pinSelected,
	handlePinDeselected,
	searchParams,
	cloudfrontUrl,
	isNewSearchVariant, // [sf16978Variation]
	onScrollToFacilityCard,
}) {
	const [searchFromMove, setSearchFromMove] = useState(false);

	const handleMapMove = useCallback(
		(loc) => {
			if (loc) {
				setSearchFromMove(true);
				let locationParams = {
					...searchParams,
					location: `${loc.latitude}, ${loc.longitude}`,
				};
				locationParams = omit(locationParams, ['page', 'pageType']);
				handleSearch({ locationParams });
			}
		},
		[handleSearch, searchParams],
	);

	const variantAttr = isNewSearchVariant ? 'new' : 'old'; // [sf16978Variation]
	return (
		<div
			className={classnames('search-map', className)}
			data-variant={variantAttr}
		>
			<noscript>
				<Text
					weight="bold"
					className="noscript-message"
				>
					Please enable JavaScript to use the interactive map.
				</Text>
			</noscript>

			<GoogleMap
				center={location}
				locationBounds={facilities.map((facility) => facility.location)}
				onMove={handleMapMove}
				radius={5}
				searchFromMove={searchFromMove}
				setSearchFromMove={setSearchFromMove}
			>
				{facilities.map((facility) => {
					const isDesktop =
						isBrowser() && isLarge(globalThis.innerWidth);
					const showMiniCard =
						facilitySelected === facility.id &&
						pinSelected &&
						(!isNewSearchVariant || !isDesktop); // suppress on desktop in new variant [sf16978Variation]
					return (
						<SearchMarker
							key={`search-marker-${facility.id}`}
							facility={facility}
							baseUrl={baseUrl}
							cloudfrontUrl={cloudfrontUrl}
							handleFacilitySelected={handleFacilitySelected}
							handleFacilityDeselected={handleFacilityDeselected}
							handlePinSelected={handlePinSelected}
							handlePinDeselected={handlePinDeselected}
							location={location}
							pinSelected={pinSelected}
							selected={facilitySelected === facility.id}
							// the mini facility card should only be shown if the pin is selected
							// but not if the facility card in the search results is the one selected
							showMiniCard={showMiniCard}
							searchType={searchType}
							searchParams={searchParams}
							isNewSearchVariant={isNewSearchVariant} // [sf16978Variation]
							onScrollToFacilityCard={onScrollToFacilityCard}
						/>
					);
				})}
			</GoogleMap>
		</div>
	);
}

SearchMap.propTypes = {
	className: PropTypes.string,
	facilities: PropTypes.arrayOf(PropTypes.object).isRequired,
	baseUrl: PropTypes.string,
	facilitySelected: PropTypes.number,
	handleFacilityDeselected: PropTypes.func,
	handleFacilitySelected: PropTypes.func,
	handlePinSelected: PropTypes.func,
	handleSearch: PropTypes.func,
	location: PropTypes.object.isRequired,
	searchType: PropTypes.string,
	pinSelected: PropTypes.bool,
	handlePinDeselected: PropTypes.func,
	searchParams: PropTypes.object,
	cloudfrontUrl: PropTypes.string,
	isNewSearchVariant: PropTypes.bool,
	onScrollToFacilityCard: PropTypes.func,
};

export const enhance = compose((WrappedComponent) => {
	const WithClickOutsideProps = (props) => {
		const outsideClickHandler = useCallback(() => {
			props.handleFacilityDeselected();
			props.handlePinDeselected();
		}, [props.handleFacilityDeselected, props.handlePinDeselected]); // eslint-disable-line react-hooks/exhaustive-deps

		const outsideParent = props.isNewSearchVariant
			? '.search-overlay-card'
			: '.new-facility-card';

		return (
			<WrappedComponent
				{...props}
				outsideClickHandler={outsideClickHandler}
				outsideParent={outsideParent}
				outsideCondition={props.pinSelected}
			/>
		);
	};
	WithClickOutsideProps.propTypes = {
		handleFacilityDeselected: PropTypes.func,
		handlePinDeselected: PropTypes.func,
		isNewSearchVariant: PropTypes.bool,
		pinSelected: PropTypes.bool,
	};
	return WithClickOutsideProps;
}, withClickOutside);

export default enhance(SearchMap);
